import { List, Map, Seq } from 'immutable'
import toString from 'lodash-es/toString'
import toUpper from 'lodash-es/toUpper'
import { Action } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { AUTOSUGGEST_ADDRESSES, AUTOSUGGEST_CUSTOMERS, GET_NUMBER_SERIES_VALUES } from '../../actions/actionTypes'
import { convert, MeasurementUnit } from '../../domain/measurementUnits'
import { IOptimusService } from '../../domain/optimusService'
import { RouteComponentProps } from '../../hooks/withRouter'
import { api } from '../../http/httpHelper'
import {
  Consignment,
  DangerousGoods,
  Department,
  DepartmentIdType,
  Order,
  OrderIdType
} from '../../types/coreEntitiesTypes'
import { ImmutableMap, ImmutableMapFactory, isNotNullOrUndefined } from '../../types/immutableTypes'
import { AppStateType } from '../../utils/appStateReduxStore'
import { isEmpty } from '../../utils/collectionUtils'
import { hourMinute } from '../../utils/dateTime'
import {
  emptyVAS,
  ensureListWithOneItem,
  filterOrderCorrectionVas,
  formValuesToGroupPickup,
  formValuesToGroupRecipient,
  getPhonePrefixFromDepartment,
  isPureVas
} from './bookingOrderFunctions'
import {
  DangerousGoodsFormikProps,
  ICustomerEventEmail,
  IMeasurement,
  IOrderForm,
  IOrderFormProps,
  MeasurementProps,
  newVAS,
  ValueAddedService
} from './bookingOrderTypes'
import { GroupRecipientProps } from './multistopTypes'
import { getServiceId } from './serviceFinders'

export enum OrderAction {
  Create = 0,
  Edit = 1,
  Copy = 2,
  Restore = 3
}

export enum BookingType {
  Standard = 0,
  H2 = 1
}

export interface BookingStateProps {
  consignments: Seq.Indexed<Consignment>
  departmentId: DepartmentIdType
  department: Department
  orderId: OrderIdType
  order?: Order
  isLoading: boolean
  services: List<IOptimusService>
  customizedServices: List<IOptimusService>
  orderNote: string | undefined
  isCopyDeletedPage?: boolean
  adrGoods?: List<DangerousGoods>
  selectedServiceId?: string
  canViewPrice: boolean
}

export interface BookingDispatchProps {
  resetForm: (formName: string) => void
  preLoadForm: (formName: string, values: Map<string, any>) => void
  mergeFormValues: (model: string, values: Map<string, any>) => void
  getOrderByIdUncached?: (orderId: OrderIdType) => any
  getDeletedOrderByIdUncached?: (orderId: OrderIdType) => any
  setPageStateValue: (key: string, value: any) => void
  getServicesFromOptimus: (
    customerNumber: string,
    subcustomer: string | null,
    pickupCountryCode: string,
    pickupPostalCode: string,
    deliveryCountryCode?: string,
    deliveryPostalCode?: string
  ) => void
  getHdServices: (
    customerNumber: string,
    subcustomer: string | null,
    deliveryCountryCode: string,
    deliveryPostalCode: string
  ) => void
  subcustomersByAlystraId: (alystraId: string) => void
  resetOrderPriceInfo?: () => void
}

export interface EditBookingDispatchProps extends BookingDispatchProps {
  getInvoicedOrderPriceData: (orderId: OrderIdType) => void
}

type PathParamsType = { departmentId: string; orderId: string }
export type BookingOwnProps = RouteComponentProps<PathParamsType>

const SMS_VAS = newVAS('1091')

export const getValueAddedServices = (
  consignment: Consignment | undefined,
  hasSms: boolean,
  shouldFilterOrderCorrectionVas: boolean
) => {
  const valueAddedServices = (consignment?.get('vasDetails') || List()).concat(
    hasSms ? List([SMS_VAS]) : List()
  ) as List<ValueAddedService>

  return valueAddedServices
    ? ensureListWithOneItem(
        valueAddedServices
          .filter(isNotNullOrUndefined)
          .filter(isPureVas)
          .filter((vas) => (shouldFilterOrderCorrectionVas ? filterOrderCorrectionVas(vas) : true)),
        emptyVAS
      )
    : List([emptyVAS()])
}

export function initForCopyOrEdit(compInst: React.Component<BookingProps>, formName: string, orderAction: OrderAction) {
  const firstConsignment = compInst.props.consignments.get(0)
  const { order, adrGoods } = compInst.props
  if (!firstConsignment) {
    return Promise.reject(new Error('No consignments found'))
  } else if (!order) {
    return Promise.reject(new Error('Order not found'))
  }

  const pickupCountry = toUpper(firstConsignment.get('pickupCountry'))
  const pickupZipCode = firstConsignment.get('pickupZipCode')
  const deliveryCountry = toUpper(firstConsignment.get('deliveryCountry'))
  const deliveryZipCode = firstConsignment.get('deliveryZipCode')
  const customerNumber = firstConsignment.get('custAlystraId')
  const subcustomer = firstConsignment.get('custAlystraSubcustomer')
  const pickupName = firstConsignment.get('pickupName')
  const deliveryName = firstConsignment.get('deliveryName')
  compInst.props.setPageStateValue(AUTOSUGGEST_CUSTOMERS + 'createOrderForm.customerId', customerNumber)
  compInst.props.setPageStateValue('selectedCustomerId', order.get('customerId'))
  compInst.props.setPageStateValue(AUTOSUGGEST_ADDRESSES + 'createOrderForm.pickupName', pickupName)
  compInst.props.setPageStateValue(AUTOSUGGEST_ADDRESSES + 'createOrderForm.deliveryName', deliveryName)
  if (firstConsignment && pickupCountry && firstConsignment.get('pickupZipCode')) {
    compInst.props.getServicesFromOptimus(
      customerNumber,
      subcustomer,
      pickupCountry,
      pickupZipCode,
      deliveryCountry,
      deliveryZipCode
    )
  }

  const behaveLikeCopy = [OrderAction.Copy, OrderAction.Restore].includes(orderAction)

  // glow-orders expects sms to be added as a vas, but it is just a bool on consignments, so add a vas here
  const hasSms = firstConsignment.get('deliverySms')
  const valueAddedServices = getValueAddedServices(firstConsignment, hasSms, behaveLikeCopy)

  return compInst.props.mergeFormValues(
    formName,
    ImmutableMapFactory<IOrderFormProps>({
      emailId: order.get('emailId'),
      createdEvent: order.get('createdEvent'),
      collectedEvent: order.get('collectedEvent'),
      deliveredEvent: order.get('deliveredEvent'),
      pickupName: firstConsignment.get('pickupName'),
      pickupAddress: firstConsignment.get('pickupAddress') || '',
      pickupZipArea: firstConsignment.get('pickupZipArea'),
      pickupZipCode: firstConsignment.get('pickupZipCode'),
      pickupAddressLat: firstConsignment.get('pickupLat'),
      pickupAddressLng: firstConsignment.get('pickupLng'),
      pickupResolvedBy:
        orderAction === OrderAction.Copy
          ? 'OriginalOrder'
          : orderAction === OrderAction.Restore
            ? 'RestoredOrder'
            : null,
      pickupCountryCode: pickupCountry,
      pickupPhone: firstConsignment.get('pickupPhone'),
      pickupEmail: firstConsignment.get('pickupEmail'),
      pickupSecondPhone: firstConsignment.get('pickupSecondPhone'),
      pickupContactPerson: firstConsignment.get('pickupContactPerson') || '',
      pickupPupUnitId: firstConsignment.get('pickupPupUnitId') || '',
      deliveryName: firstConsignment.get('deliveryName'),
      deliveryAddress: firstConsignment.get('deliveryAddress') || '',
      deliveryZipArea: firstConsignment.get('deliveryZipArea'),
      deliveryZipCode: firstConsignment.get('deliveryZipCode'),
      deliveryAddressLat: firstConsignment.get('deliveryLat'),
      deliveryAddressLng: firstConsignment.get('deliveryLng'),
      deliveryResolvedBy:
        orderAction === OrderAction.Copy
          ? 'OriginalOrder'
          : orderAction === OrderAction.Restore
            ? 'RestoredOrder'
            : null,
      deliveryCountryCode: deliveryCountry,
      deliveryPhone: firstConsignment.get('deliveryPhone'),
      deliveryEmail: firstConsignment.get('deliveryEmail'),
      deliverySecondPhone: firstConsignment.get('deliverySecondPhone'),
      deliveryContactPerson: firstConsignment.get('deliveryContactPerson') || '',
      deliveryPupUnitId: firstConsignment.get('deliveryPupUnitId') || '',
      customerRef: firstConsignment.get('customerRef') || '',
      recipientRef: firstConsignment.get('recipientRef') || '',
      pickupInstructions: firstConsignment.get('pickupInstructions'),
      deliveryInstructions: firstConsignment.get('deliveryInstructions'),
      orderNote: compInst.props.orderNote,
      customerId: firstConsignment.get('custAlystraId'),
      subcustomer: firstConsignment.get('custAlystraSubcustomer'),
      customerInfo: firstConsignment.get('custInformation'),
      serviceId: getServiceId(firstConsignment, compInst.props.services, compInst.props.customizedServices),
      pickupTimeEarliest: hourMinute(firstConsignment.get('pickupTimeEarliestCached')),
      // @ts-expect-error TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
      pickupDate: firstConsignment.get('pickupTimeEarliestCached')?.toISODate(),
      pickupTimeLatest: hourMinute(firstConsignment.get('pickupTimeLatestCached')),
      deliveryTimeEarliest: hourMinute(firstConsignment.get('deliveryTimeEarliestCached')),
      deliveryTimeLatest: hourMinute(firstConsignment.get('deliveryTimeLatestCached')),
      deliveryTimeWindowId: undefined,
      manualInvoiceInfo: order.get('manualInvoiceInfo'),
      invoiceInfo: order.get('invoiceInfo'),
      preplannedUnitId: firstConsignment.get('preplannedToUnit'),
      preplannedTemplateSlotId: order.get('preplannedTemplateSlotId')?.toString() || '',
      selectedDepartmentId: order.get('departmentIdOverridden') ? order.get('departmentId')?.toString() : null,
      packageIds: behaveLikeCopy ? List() : compInst.props.consignments.map((c) => c.get('packageId')).toList(),
      groupId: behaveLikeCopy ? '' : order.get('groupId'),
      groupName: behaveLikeCopy ? '' : order.get('groupName'),
      groupOrder: behaveLikeCopy ? undefined : order.get('groupOrder'),
      paidTogether: behaveLikeCopy ? undefined : order.get('paidTogether'),
      vehicleType: order.get('vehicleType') || '',
      customerContact: order.get('customerContact') || '',
      measurements: compInst.props.consignments
        .map((c: Consignment) => {
          return Map({
            packageId: toString(c.get('packageId')),
            weight: toString(c.get('weightValue')) || '0',
            height: toString(c.get('heightValue')) || '0',
            length: toString(c.get('lengthValue')) || '0',
            width: toString(c.get('widthValue')) || '0',
            presetDimension: toString(c.get('presetDimension')),
            specificationDescription: c.get('specificationDescription'),
            volume: convert(c.get('volumeUnit'), MeasurementUnit.DMQ, c.get('volumeValue'))?.toString() || '0',
            packageTypeCode: c.get('packageTypeCode'),
            palletFootPrints: c.get('palletFootprintsValue'),
            loadingMeters: c.get('loadingMetersValue'),
            quantity: '1'
          }) as IMeasurement
        })
        .toList(),
      additionalServices: valueAddedServices,
      adrGoods,
      correctedArticlePrices: List(),
      invoiceSplit: order.get('invoiceSplit') ?? '',
      standaloneInvoice: order.get('standaloneInvoice') ?? false
    }).filter(isNotNullOrUndefined) as IOrderForm
  )
}

export type BookingProps = BookingStateProps & BookingDispatchProps
export type EditBookingProps = BookingStateProps & EditBookingDispatchProps

export const pickupComponentNames = List([
  'pickupName',
  'pickupLocation',
  'pickupAddress',
  'pickupZipCode',
  'pickupZipArea',
  'pickupCountryCode',
  'pickupContactPerson',
  'pickupPhone',
  'pickupInstructions'
])
export const deliveryComponentNames = List([
  'deliveryName',
  'deliveryLocation',
  'deliveryAddress',
  'deliveryZipCode',
  'deliveryZipArea',
  'deliveryCountryCode',
  'deliveryContactPerson',
  'deliveryPhone',
  'deliveryInstructions',
  'groupRecipients'
])
export const measurementComponentNames = List(['specificationDescription', 'measurements'])
export const serviceComponentNames = List(['serviceId', 'additionalServices'])
export const invoiceInformationComponentNames = List([
  'customerContact',
  'customerRef',
  'recipientRef',
  'invoiceInfo',
  'manualInvoiceInfo'
])
export const bookingPageComponentNames = List(['customerId', 'subcustomer', 'customerInfo']).concat(
  pickupComponentNames,
  deliveryComponentNames,
  measurementComponentNames,
  serviceComponentNames,
  invoiceInformationComponentNames
)

export const getCustomerEventEmailsData = (state: AppStateType): List<ICustomerEventEmail> =>
  state.getIn(['entities', 'customerEventEmails']) || List()

export const mergeDangerousGoods = (
  adrGoods: List<DangerousGoods> | DangerousGoodsFormikProps[] | undefined,
  dangerousGoods: List<DangerousGoods>
) =>
  dangerousGoods.map((dangerousGood) => {
    const foundGoods = adrGoods?.find((it) =>
      'get' in it ? it.get('id') === dangerousGood.get('id') : it.id === dangerousGood.get('id')
    )
    return foundGoods ? foundGoods : dangerousGood
  })

export const acceptableMeasurements = (measurements: List<IMeasurement> | MeasurementProps[]) =>
  (List.isList(measurements) ? measurements.toArray() : measurements).every(
    (m: any) =>
      (('get' in m ? m.get('length') : m.length) === '0' &&
        ('get' in m ? m.get('width') : m.width) === '0' &&
        ('get' in m ? m.get('height') : m.height) === '0') ||
      (('get' in m ? m.get('length') : m.length) !== '0' &&
        ('get' in m ? m.get('width') : m.width) !== '0' &&
        ('get' in m ? m.get('height') : m.height) !== '0')
  )

interface Props {
  formValues: IOrderForm
  department: Department
}
export const getFirstPickup = (props: Props) => getAllPickups(props).first() as ImmutableMap<GroupRecipientProps>

export const getAllPickups = (props: Props) => {
  const groupPickups = props.formValues.get('groupPickups') || List<ImmutableMap<GroupRecipientProps>>()
  return hasAtLeastOneAdditionalPickup(props.formValues) && pickupFieldsAreEmpty(props.formValues)
    ? groupPickups
    : groupPickups.push(formValuesToGroupPickup(props.formValues, getPhonePrefixFromDepartment(props.department)))
}

export const getFirstRecipient = (props: Props) => getAllRecipients(props).first() as ImmutableMap<GroupRecipientProps>

export const getAllRecipients = (props: Props) => {
  const groupRecipients = props.formValues.get('groupRecipients') || List<ImmutableMap<GroupRecipientProps>>()
  return hasAtLeastOneAdditionalRecipient(props.formValues) && recipientFieldsAreEmpty(props.formValues)
    ? groupRecipients
    : groupRecipients.push(formValuesToGroupRecipient(props.formValues, getPhonePrefixFromDepartment(props.department)))
}
export const recipientFields = List.of<keyof IOrderFormProps>(
  'deliveryName',
  'deliveryAddress',
  'deliveryZipArea',
  'deliveryZipCode',
  'deliveryCountryCode',
  'deliveryPhone',
  'deliveryContactPerson',
  'deliveryInstructions',
  'recipientRef',
  'deliveryEmail'
)

export const pickupFields = List.of<keyof IOrderFormProps>(
  'pickupName',
  'pickupAddress',
  'pickupZipArea',
  'pickupZipCode',
  'pickupCountryCode',
  'pickupPhone',
  'pickupEmail',
  'pickupContactPerson',
  'pickupInstructions',
  'customerRef'
)
export const recipientFieldsAreEmpty = (formValues: IOrderForm) =>
  recipientFields.every((f) => formFieldIsEmpty(formValues, f))
export const pickupFieldsAreEmpty = (formValues: IOrderForm) =>
  pickupFields.every((f) => formFieldIsEmpty(formValues, f))

export const hasAtLeastOneAdditionalRecipient = (formValues: IOrderForm) =>
  !(formValues.get('groupRecipients') || List()).isEmpty()
export const hasAtLeastOneAdditionalPickup = (formValues: IOrderForm) =>
  !(formValues.get('groupPickups') || List()).isEmpty()

const formFieldIsEmpty = (formValues: IOrderForm, fieldName: keyof IOrderFormProps) => {
  const value = formValues.get(fieldName)
  return isEmpty(value) || toString(value).startsWith('+')
}

export function getShipmentAndPackageIds(numShipments = 1, numPackages: number) {
  return (dispatch: ThunkDispatch<AppStateType, null, Action>) => {
    return dispatch(
      api.asyncPostJson(GET_NUMBER_SERIES_VALUES, '/number-series', {
        GSIN: numShipments,
        SSCC: numPackages
      })
    )
  }
}
